import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Car,
  Phone,
  Scan,
  FileText,
  TrendingUp,
  Home,
  ClipboardCheck,
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  isActive: boolean;
}

interface BottomNavigationProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
  /** The tabs to show, in order (useDriverTabs). */
  tabs: readonly string[];
}

const ALL_NAV_ITEMS: Omit<NavItem, 'isActive'>[] = [
  { id: 'home', label: 'Home', icon: Home },
  { id: 'drivers', label: 'Dispatch', icon: Phone },
  { id: 'calls', label: 'Calls', icon: Phone },
  { id: 'impounds', label: 'Impounds', icon: Car },
  { id: 'invoices', label: 'Invoices', icon: FileText },
  { id: 'inspections', label: 'Inspections', icon: ClipboardCheck },
  { id: 'scanner', label: 'Scanner', icon: Scan },
  { id: 'analytics', label: 'Analytics', icon: TrendingUp },
];

const BottomNavigation: React.FC<BottomNavigationProps> = ({
  activeTab,
  onTabChange,
  tabs,
}) => {
  const navigate = useNavigate();

  const navItems: NavItem[] = ALL_NAV_ITEMS.filter((item) =>
    tabs.includes(item.id)
  ).map((item) => ({
    ...item,
    isActive: activeTab === item.id,
  }));

  const handleTabClick = (tabId: string) => {
    // Inspections is a route of its own rather than a tab of the home screen:
    // it has a run flow and a report view underneath it.
    if (tabId === 'inspections') {
      navigate('/inspections');
      return;
    }
    onTabChange(tabId);
  };

  // Six tabs do not fit a 375px screen at the five-tab padding, so the bar
  // tightens instead of letting a label spill into its neighbour.
  const isTight = navItems.length > 5;

  return (
    <div
      className={`bg-background border-t border-gray-200 py-3 flex justify-around ${
        isTight ? 'px-1' : 'px-4'
      }`}
    >
      {navItems.map((item) => {
        const IconComponent = item.icon;
        return (
          <button
            key={item.id}
            className={`flex flex-col items-center py-2 transition-colors hover:bg-gray-50 rounded-lg min-w-0 ${
              isTight ? 'px-1' : 'px-3'
            }`}
            onClick={() => handleTabClick(item.id)}
          >
            <IconComponent
              size={isTight ? 22 : 24}
              className={`mb-1 ${item.isActive ? '' : 'text-gray-600'}`}
              style={item.isActive ? { color: '#45A17D' } : {}}
              fill={item.isActive ? 'currentColor' : 'none'}
            />
            <span
              className={`font-medium max-w-full truncate ${
                isTight ? 'text-[10px]' : 'text-xs'
              } ${item.isActive ? '' : 'text-gray-600'}`}
              style={item.isActive ? { color: '#45A17D' } : {}}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default BottomNavigation;
